import React, { useRef, useEffect, useSyncExternalStore } from 'react'
import PropTypes from 'prop-types'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import MuiDialog from '@mui/material/Dialog'
import MuiDialogTitle from '@mui/material/DialogTitle'
import MuiDialogContent from '@mui/material/DialogContent'
import MuiDialogActions from '@mui/material/DialogActions'

const SwalWithReact = withReactContent(Swal)

// Focus arbitration between SweetAlert popups and MUI dialogs. An open MUI Dialog enforces focus
// inside itself, so a SweetAlert popup opened on top of it (Dialog.prompt from inside a
// Dialog.custom, e.g. "Unlock controls") had every focus pulled back into the MUI dialog: typing
// went nowhere and the prompt resolved with an empty value. While any SweetAlert popup is open,
// CustomDialog stops enforcing focus (disableEnforceFocus) and the popup's input gets focus.
let openSwalCount = 0
const swalListeners = new Set()
const swalStore = {
  subscribe: (listener) => {
    swalListeners.add(listener)
    return () => swalListeners.delete(listener)
  },
  isOpen: () => openSwalCount > 0,
}
const setSwalOpen = (delta) => {
  openSwalCount = Math.max(0, openSwalCount + delta)
  swalListeners.forEach((listener) => listener())
}

const withFocusArbitration = (options) => {
  if (!options || typeof options !== 'object') return options
  const { willOpen, didOpen, didDestroy, ...rest } = options
  return {
    ...rest,
    willOpen: (popup) => {
      setSwalOpen(1)
      willOpen?.(popup)
    },
    didOpen: (popup) => {
      didOpen?.(popup)
      // After CustomDialog has re-rendered without its focus trap, give the popup its focus back.
      setTimeout(() => (Swal.getInput() || Swal.getConfirmButton())?.focus(), 0)
    },
    didDestroy: () => {
      setSwalOpen(-1)
      didDestroy?.()
    },
  }
}

// Every popup goes through focus arbitration, whichever helper fired it. (Explicit wrapper —
// spreading the Swal class would drop its inherited static methods.)
const MySwal = {
  fire: (options) => SwalWithReact.fire(withFocusArbitration(options)),
  showLoading: () => SwalWithReact.showLoading(),
  close: () => SwalWithReact.close(),
}

// Matches DefaultModal's Overlay z-index (1300) + headroom, real replacement for the dead swal-high-z-index class.
const ELEVATED_Z_INDEX = 1400

const resolveConfirmColor = (confirmColor) => confirmColor || 'var(--vo-danger)'

const withElevated = (options, elevated) => {
  if (!elevated) return options
  const { didOpen, ...rest } = options
  return {
    ...rest,
    didOpen: (popup) => {
      popup.parentNode.style.zIndex = ELEVATED_Z_INDEX
      didOpen?.(popup)
    },
  }
}

const alert = ({ title, message, icon = 'info', confirmLabel = 'OK', elevated }) =>
  MySwal.fire(
    withElevated(
      {
        title,
        text: message,
        icon,
        confirmButtonText: confirmLabel,
        confirmButtonColor: 'var(--color-brand)',
      },
      elevated
    )
  )

const confirm = ({ title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', confirmColor, elevated }) =>
  MySwal.fire(
    withElevated(
      {
        title,
        text: message,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: confirmLabel,
        cancelButtonText: cancelLabel,
        confirmButtonColor: resolveConfirmColor(confirmColor),
        cancelButtonColor: 'var(--text-muted)',
        reverseButtons: true,
      },
      elevated
    )
  ).then((result) => result.isConfirmed)

const prompt = ({
  title,
  message,
  inputLabel,
  inputPlaceholder,
  inputValidator,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  elevated,
}) =>
  MySwal.fire(
    withElevated(
      {
        title,
        text: message,
        input: 'textarea',
        inputLabel,
        inputPlaceholder,
        inputValidator,
        showCancelButton: true,
        confirmButtonText: confirmLabel,
        cancelButtonText: cancelLabel,
        confirmButtonColor: 'var(--color-brand)',
        cancelButtonColor: 'var(--text-muted)',
        reverseButtons: true,
      },
      elevated
    )
  ).then((result) => (result.isConfirmed ? result.value ?? '' : null))

// Always opens a real popup before showLoading() — fixes the 2 pre-existing call sites
// that called SwalReact.showLoading() with no .fire() to attach to.
const loading = ({ title, message } = {}) =>
  MySwal.fire({
    title,
    text: message,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    willOpen: () => MySwal.showLoading(),
  })

const close = () => MySwal.close()

// Approximates DefaultModal's pixel widths (480/520/900/1100) via MUI's maxWidth breakpoints.
const SIZE_TO_MAX_WIDTH = { sm: 'xs', md: 'sm', lg: 'md', xl: 'lg' }

const CustomDialog = ({ open, onClose, title, size, children, actions, elevated }) => {
  const swalOpen = useSyncExternalStore(swalStore.subscribe, swalStore.isOpen)
  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth={SIZE_TO_MAX_WIDTH[size] || 'sm'}
      fullWidth
      disableEnforceFocus={swalOpen}
      sx={elevated ? { zIndex: ELEVATED_Z_INDEX } : undefined}
    >
      {title && <MuiDialogTitle>{title}</MuiDialogTitle>}
      <MuiDialogContent dividers>{children}</MuiDialogContent>
      {actions && <MuiDialogActions>{actions}</MuiDialogActions>}
    </MuiDialog>
  )
}

CustomDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  children: PropTypes.node,
  actions: PropTypes.node,
  elevated: PropTypes.bool,
}

CustomDialog.defaultProps = {
  open: false,
  size: 'md',
}

export const Dialog = ({
  open,
  onConfirm,
  onCancel,
  title,
  message,
  confirmLabel,
  cancelLabel,
  confirmColor,
}) => {
  const firedRef = useRef(false)

  useEffect(() => {
    if (!open) {
      firedRef.current = false
      return undefined
    }

    firedRef.current = false

    MySwal.fire({
      title,
      text: message,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: confirmLabel,
      cancelButtonText: cancelLabel,
      confirmButtonColor: resolveConfirmColor(confirmColor),
      cancelButtonColor: 'var(--text-muted)',
      reverseButtons: true,
      allowOutsideClick: true,
      preConfirm: () => {
        if (firedRef.current) {
          return false
        }
        firedRef.current = true
      },
    }).then((result) => {
      if (result.isConfirmed) {
        onConfirm()
      } else if (result.dismiss === Swal.DismissReason.cancel || result.dismiss === Swal.DismissReason.backdrop) {
        onCancel()
      }
      firedRef.current = false
    })

    return () => {
      Swal.close()
      firedRef.current = false
    }
  }, [open, onConfirm, onCancel, title, message, confirmLabel, cancelLabel, confirmColor])

  return null
}

Dialog.propTypes = {
  open: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  confirmColor: PropTypes.string,
}

Dialog.defaultProps = {
  open: false,
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  confirmColor: undefined,
}

// Raw passthrough escape hatch — keeps SweetAlert2 an implementation detail
// while still "routing through" this component per SPEC-UI-001 FR-4.
Dialog.fire = (options) => MySwal.fire(options)
Dialog.alert = alert
Dialog.confirm = confirm
Dialog.prompt = prompt
Dialog.loading = loading
Dialog.close = close
Dialog.custom = CustomDialog
